import { Flex, Spinner } from '@radix-ui/themes';
import { MachineContextHelper } from 'classes/helpers/machine-context.helper';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { WebSocketHelper } from 'classes/helpers/web-socket.helper';
import { CommonCallout } from 'components/common/callouts';
import { CommonDialog } from 'components/common/dialogs';
import { DialogButton } from 'components/common/dialogs/button';
import { ErrorBoundary } from 'components/common/error-boundary';
import { MachineCameraStreamViewer } from 'components/machine/camera-stream-viewer';
import {
  StreamingContext,
  StreamingProvider,
} from 'components/machine/camera-stream-viewer/context';
import { MachineContext } from 'contexts/machine.context';
import { addSeconds, isFuture } from 'date-fns';
import { useFeatureFlag } from 'hooks/useFeatureFlag';
import { t } from 'i18next';
import { IButton } from 'interfaces/i-buttons';
import { IBaseDialog } from 'interfaces/i-dialogs';
import { IStreamProps } from 'interfaces/i-streaming';
import {
  CameraSource,
  StaticVideoType,
  TroubleshootingPitchID,
  WsMsgType,
} from 'lib_ts/enums/machine-msg.enum';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IBallStatusMsg } from 'lib_ts/interfaces/machine-msg/i-ball-status';
import { SpecialMsPosition } from 'lib_ts/interfaces/machine-msg/i-special-mstarget';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { WebSocketService } from 'services/web-socket.service';
import useDropBallStore from 'stores/use-drop-ball-store';
import useMachineStatusStore from 'stores/use-machine-status-store';

const COMPONENT_NAME = 'MachineInspectionDialog';

const FF_DEFAULT_INSPECT_SOURCE_BI_RIGHT = 'default-inspect-source-bi-right';

const useDefaultInspectSource = (): CameraSource => {
  const biRight = useFeatureFlag(FF_DEFAULT_INSPECT_SOURCE_BI_RIGHT);
  return biRight ? CameraSource.BI_RIGHT : CameraSource.BI_R;
};

const EJECT_BALL_RPM = 400;

const EJECT_BALL_YAW = 2;

// how many seconds into the future should the app wait before acting upon any r2f from the machine after sending the ejection mstarget
const MIN_WAIT_BEFORE_EJECTING_SEC = 2;

// no need to provide IBaseDialog props when rendering as a standalone component
interface IDialogProps extends IStreamProps, IBaseDialog {
  asStandalone?: boolean;
  hideSource?: boolean;
}

export const MachineInspectionStandaloneHoC = (props: IStreamProps) => {
  const defaultSource = useDefaultInspectSource();

  return (
    <StreamingProvider
      machineID={props.machineID}
      hwVersion={props.hwVersion}
      defaultSource={defaultSource}
    >
      <MachineInspectionDialog
        identifier="MachineInspectorStandalone"
        machineID={props.machineID}
        hwVersion={props.hwVersion}
        mode={props.mode}
        onClose={() => {
          // do nothing, since it's a standalone
        }}
        asStandalone
        hideSource
      />
    </StreamingProvider>
  );
};

export const MachineInspectionDialogHoC = (props: IDialogProps) => {
  const defaultSource = useDefaultInspectSource();

  return (
    <StreamingProvider
      machineID={props.machineID}
      hwVersion={props.hwVersion}
      defaultSource={defaultSource}
    >
      <MachineInspectionDialog {...props} />
    </StreamingProvider>
  );
};

interface IStandaloneProps {
  content: React.ReactNode;
  buttons: IButton[];
}

const MachineInspectionStandalone = ({
  content,
  buttons,
}: IStandaloneProps) => (
  <Flex direction="column" gap={RADIX.FLEX.GAP.MD}>
    {content}
    <Flex gap={RADIX.FLEX.GAP.SM} justify="end">
      {buttons
        .filter((b) => !b.invisible)
        .map((b) => (
          <DialogButton key={b.label} {...b} />
        ))}
    </Flex>
  </Flex>
);

const MachineInspectionDialog = (props: IDialogProps) => {
  const r2fData = useMachineStatusStore((s) => s.r2fData);

  const { paused, lastFrame } = useContext(StreamingContext);

  // should only be used when props.mode === 'self'
  const { machine, dropball, sendTarget, specialMsTarget, setSpecialMode } =
    useContext(MachineContext);

  // prevents additional drop ball triggers until a ball-status is received
  const droppingBall = useDropBallStore((s) => s.droppingBall);

  const autoCloseTimeout = useRef<NodeJS.Timeout>();

  useEffect(() => {
    return () => {
      clearTimeout(autoCloseTimeout.current);
    };
  }, []);

  const [ejecting, setEjecting] = useState(false);
  // skips r2f until after this date
  const [ignoreUntil, setIgnoreUntil] = useState(new Date());

  useEffect(() => {
    if (props.mode === 'self') {
      setSpecialMode('inspect-machine');
    }

    return () => {
      if (props.mode === 'self') {
        setSpecialMode(undefined);
      }
    };
  }, [props.mode]);

  const statusCallback = useCallback(
    (event: CustomEvent) => {
      if (ejecting) {
        // ignore ball status updates while ejecting
        return;
      }

      const data: IBallStatusMsg = event.detail;

      if (data.ball_count !== 1) {
        return;
      }

      autoCloseTimeout.current = setTimeout(() => {
        // only provided when rendering as a dialog
        if ('onClose' in props) {
          props.onClose();
        }
      }, 3_000);
    },
    [ejecting]
  );

  useEffect(() => {
    WebSocketHelper.on(WsMsgType.M2U_BallStatus, statusCallback);
    return () =>
      WebSocketHelper.remove(WsMsgType.M2U_BallStatus, statusCallback);
  }, [statusCallback]);

  // listen to r2fData to determine when we can eject balls
  useEffect(() => {
    if (props.mode !== 'self') {
      return;
    }

    if (!ejecting) {
      return;
    }

    if (isFuture(ignoreUntil)) {
      return;
    }

    if (!r2fData) {
      return;
    }

    // * IMPORTANT: check only the relevant procs instead of the overall status flag
    // * REASON: for particular (e.g. extremely low) values, machine won't report overall r2f
    const { w1, w2, w3, px, pz, yaw } = r2fData.data;

    if ([w1, w2, w3, px, pz, yaw].includes(false)) {
      return;
    }

    NotifyHelper.warning({
      message_md: 'Attempting to eject ball(s) from inserter, please wait...',
    });

    setEjecting(false);
    WebSocketService.send(WsMsgType.U2S_EjectBalls, {}, COMPONENT_NAME);
  }, [props.mode, ejecting, ignoreUntil, r2fData]);

  const validLastFrame = !!(lastFrame && lastFrame.buffer);

  const buttons = [
    {
      label: 'main.drop-ball',
      icon: droppingBall ? <Spinner /> : undefined,
      color: RADIX.COLOR.WARNING,
      disabled: droppingBall || props.mode === 'other' || !validLastFrame,
      onClick: () =>
        dropball({
          source: COMPONENT_NAME,
          checkActive: true,
          notifyResult: true,
        }),
    },
    {
      label: 'main.eject-balls',
      color: RADIX.COLOR.WARNING,
      // only the active user the machine being inspected can eject balls
      invisible: props.mode !== 'self',
      tooltip: paused
        ? 'Please resume the stream before ejecting'
        : !lastFrame
          ? 'Please wait for the stream to load before ejecting'
          : undefined,
      disabled: paused || !validLastFrame,
      onClick: async () => {
        if (ejecting) {
          return;
        }

        const data = MachineContextHelper.getTroubleshootingMS(machine, {
          id: TroubleshootingPitchID.EjectBalls as string,
          video_uuid: StaticVideoType.screensaver,
          wheels: {
            w1: EJECT_BALL_RPM,
            w2: EJECT_BALL_RPM,
            w3: EJECT_BALL_RPM,
          },
          yaw: EJECT_BALL_YAW,
          rapid: true,
        });

        if (!data.msg) {
          return;
        }

        const ignoreUntil = addSeconds(
          new Date(),
          MIN_WAIT_BEFORE_EJECTING_SEC
        );

        setIgnoreUntil(ignoreUntil);

        const sendResult = await sendTarget({
          source: COMPONENT_NAME,
          pitch: data.pitch,
          ref_py: data.pitch._mx_ms.py,
          msMsg: data.msg,
          force: true,
        });

        if (!sendResult.success) {
          NotifyHelper.warning({
            message_md: t('common.request-failed-msg'),
          });
          return;
        }

        setEjecting(true);
      },
    },
    {
      label: 'common.lower-machine',
      color: RADIX.COLOR.WARNING,
      // only the active user the machine being inspected can lower it
      invisible: props.mode !== 'self',
      disabled: !validLastFrame,
      onClick: () => specialMsTarget(SpecialMsPosition.lowered),
    },
  ];

  const content = (
    <Flex direction="column" gap={RADIX.FLEX.GAP.LG}>
      <MachineCameraStreamViewer
        machineID={props.machineID}
        hideSource={props.hideSource}
        allowCrosshair
      />
      {ejecting && <CommonCallout text="Ejecting balls, please wait..." />}
    </Flex>
  );

  if (props.asStandalone) {
    return (
      <ErrorBoundary identifier={COMPONENT_NAME}>
        <MachineInspectionStandalone content={content} buttons={buttons} />
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary identifier={COMPONENT_NAME}>
      <CommonDialog
        identifier={COMPONENT_NAME}
        width={RADIX.DIALOG.WIDTH.LG}
        title={'main.inspect-machine'}
        content={content}
        buttons={buttons}
        onClose={props.onClose}
      />
    </ErrorBoundary>
  );
};
